import { Component, OnInit, AfterViewInit, OnDestroy, ElementRef, ViewChild } from '@angular/core';
import flatpickr from 'flatpickr';
import { Italian } from 'flatpickr/dist/l10n/it';
import { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';
import { Region } from '../../interfaces/Regions.interfaces';
import { PalletCloseBulkResponse, ReturnsCsvImportResponse } from '../../services/returns.service';
import { SearchableSelectComponent, SearchableSelectOption } from '../../components/searchable-select/searchable-select.component';
import { ScrollTopButtonComponent } from '../../components/scroll-top-button/scroll-top-button.component';
import { CloseReturnsPopupComponent } from './close-returns-popup/close-returns-popup.component';
import { ReturnPrintPopupComponent } from './return-print-popup/return-print-popup.component';
import { CloseManualPopupComponent } from './close-manual-popup/close-manual-popup.component';
import { PlatformExcelPopupComponent, PlatformExcelSelection } from './platform-excel-popup/platform-excel-popup.component';
import { ReturnRequestService } from '../../services/return-request.service';
import { TourGuideService } from '../../services/tour-guide.service';
import { TourGuideComponent } from '../../components/tour-guide/tour-guide.component';
import { RETURNS_TOUR_CONFIG } from '../../configs/returns-tour.config';
import { Dealer } from '../../interfaces/waybill.interfaces';
import { Pallets } from '../../interfaces/Pallets.interfaces';

export interface Carrier {
  carrierId?: number;
  carrierCode: string;
  carrierName: string;
  carrierVatNumber: string;
  carrierInternalBillingId: string;
  carrierInternalId: string;
  carrierSdiCode: string;
  carrierLegalAddressStreet: string;
  carrierLegalAddressNumber: string;
  carrierLegalAddressZip: string;
  carrierLegalAddressCity: string;
  carrierLegalAddressProvince: string;
  carrierLegalAddressCountry: string;
  carrierOperatingAddressStreet: string;
  carrierOperatingAddressNumber: string;
  carrierOperatingAddressZip: string;
  carrierOperatingAddressCity: string;
  carrierOperatingAddressProvince: string;
  carrierOperatingAddressCountry: string;
  carrierIncorporationDate: Date | null;
  carrierIndustry: string;
  carrierStatus: string;
  carrierNote?: string;
  carrierLatitude?: number;
  carrierLongitude?: number;
  carrierVersion: number;
  carrierCreatedAt: Date;
}

export interface PalletTypes {
  palletTypesCode: string;
  palletTypeName: string;
  palletTypesGroupName: string;
  palletTypesGroupCode: string;
}

export interface ViewComPallets {
  palletComplementaryValueNumber: string;
  palletComplementaryValueDate: string;
  palletComplementaryStatus: string;
  palletComplementaryWarehouseCode: string;
  warehouseCodeName: string;
  palletComplementaryCarrierCode: string;
  carrierName: string;
}

export interface ViewOprPallets {
  palletId: number;
  palletClientName: string;
  palletBrandName: string;
  palletCode: string;
  palletWaybillCode: string;
  palletShipmentCode: string;
  palletDate: string;
  palletDeliveryType: string;
  palletDealerCode: string;
  dealerName: string;
  dealerOperatingAddressStreet: string;
  dealerOperatingAddressCity: string;
  palletStatus: string;
  palletActualDeliveryDate: string;
  palletEstimatedDeliveryDate: string;
  regionCode: string;
  regionName: string;
  palletCurrentStatus: string;
  palletOnTime: string;
  showComPallets: boolean;
  palletTypesGroupName: string;
  // Parametri pallet per la stampa della label (peso, volume, note)
  palletWeight?: number;
  palletVolume?: number;
  palletNote?: string;
  // Flag 'Y'/'N': data di consegna forzata manualmente (Opr_Pallets_DTO.palletDeliveryDateIsForced)
  palletDeliveryDateIsForced?: string;
  comPallets: ViewComPallets[];
}


/** Risposta paginata di POST /pallets/search */
interface PalletsSearchResponse {
  pallets: ViewOprPallets[];
  totalCount: number;
  totalPages: number;
  page: number;
  pageSize: number;
}

interface Warehouse {
  warehouseCode: string;
  warehouseName?: string;
}

interface Client {
  clientCode: string;
  clientName?: string;
}

interface Brand {
  brandCode: string;
  brandName?: string;
}

interface ClientsBrands {
  clientCode: string;
  clientName?: string;
  brands: Brand[];
}


/** I quattro filtri data della pagina, tutti in formato yyyy-MM-dd */
type ReturnsDateField = 'dateStart' | 'dateEnd' | 'dateDeliveryStart' | 'dateDeliveryEnd';

/** Colonna della tabella resi gestita da visibilità/ordine dello strumentario */
interface ReturnsColumn {
  key: string;
  label: string;
  /** Campo di ordinamento righe lato backend; assente = colonna non ordinabile */
  sortKey?: string;
}

@Component({
  selector: 'app-returns',
  standalone: true,
  templateUrl: './returns.component.html',
  styleUrls: ['./returns.component.css'],
  imports: [FormsModule, CommonModule, TranslateModule, SearchableSelectComponent, CloseReturnsPopupComponent, ReturnPrintPopupComponent, CloseManualPopupComponent, PlatformExcelPopupComponent, TourGuideComponent, ScrollTopButtonComponent]
})
export class ReturnsComponent implements OnInit, AfterViewInit, OnDestroy {

  currentPage: number = 1;
  itemsPerPage: number = 25;
  readonly pageSizeOptions: number[] = [5, 10, 25, 50, 100];

  /** Totale dei resi filtrati a sistema (non solo quelli della pagina corrente) */
  totalCount: number = 0;

  oprPallets: ViewOprPallets[] = [];
  filteredOprPallets: ViewOprPallets[] = [];

  temp: ViewOprPallets[] = [];

  searchDealerCode: string = '';
  searchPalletCode: string = '';
  searchWaybillCode: string = '';
  dateStart: string = '';
  dateEnd: string = '';
  dateDeliveryStart: string = '';
  dateDeliveryEnd: string = '';
  onTimeStatus: string = '';

  /**
   * Toggle "Chiusura Manuale" (default spento). Da attivo la ricerca invia
   * deliveryDateIsForced: true, per avere solo i resi con data di consegna
   * forzata (palletDeliveryDateIsForced = 'Y').
   */
  filterForcedDelivery = false;

  setFilterForcedDelivery(value: boolean): void {
    this.filterForcedDelivery = value;
  }

  dateError: string = '';
  /** Intervallo a cui si riferisce l'errore, per bordare in rosso i campi coinvolti */
  dateErrorScope: 'affido' | 'consegna' | 'entrambi' | null = null;
  isLoading: boolean = false;

  // ===== Filtri data: flatpickr + navigazione giorno, come in shipment-management =====
  @ViewChild('dateStartInput') dateStartInput?: ElementRef<HTMLInputElement>;
  @ViewChild('dateEndInput') dateEndInput?: ElementRef<HTMLInputElement>;
  @ViewChild('dateDeliveryStartInput') dateDeliveryStartInput?: ElementRef<HTMLInputElement>;
  @ViewChild('dateDeliveryEndInput') dateDeliveryEndInput?: ElementRef<HTMLInputElement>;

  /** Un'istanza flatpickr per campo, creata alla prima apertura */
  private datePickers = new Map<ReturnsDateField, FlatpickrInstance>();

  /** Ricerca resi in corso: viene annullata a ogni nuova ricerca */
  private searchSubscription?: Subscription;

  globalSearchTerm: string = '';
  palletStatus: string = '';     // "DELIVERED" | "PICKED_UP"

  showFilters: boolean = true;

  // ===== Strumentario (toolbox) =====
  /** Chiave localStorage delle preferenze dello strumentario */
  private readonly STORAGE_KEY = 'returns-settings';
  /** Densità compatta della tabella resi */
  isCompactTable = false;
  /** Dropdown gestione colonne visibili */
  isColumnsDropdownOpen = false;
  /** Chiavi delle colonne nascoste (persistite) */
  hiddenColumns = new Set<string>();

  // Colonne gestibili dallo strumentario, colonna Stampa compresa.
  // sortKey è il campo di ordinamento righe passato al backend (assente = non ordinabile).
  columnOptions: ReturnsColumn[] = [
    { key: 'select', label: 'Seleziona' },
    { key: 'print', label: 'Stampa' },
    { key: 'palletClientName', label: 'Cliente', sortKey: 'palletClientName' },
    { key: 'palletBrandName', label: 'Marchio', sortKey: 'palletBrandName' },
    { key: 'palletCode', label: 'Codice Reso' },
    { key: 'palletTypesGroupName', label: 'Tipologia' },
    { key: 'palletWaybillCode', label: 'Codice Bolla' },
    { key: 'palletDate', label: 'Data Affido', sortKey: 'palletDate' },
    { key: 'palletDeliveryType', label: 'Contenuto', sortKey: 'palletDeliveryType' },
    { key: 'palletDealerCode', label: 'Codice Dealer' },
    { key: 'dealerName', label: 'Dealer', sortKey: 'dealerName' },
    { key: 'dealerAddress', label: 'Indirizzo Dealer' },
    { key: 'palletActualDeliveryDate', label: 'Data Consegna', sortKey: 'palletActualDeliveryDate' },
    { key: 'palletEstimatedDeliveryDate', label: 'Data Stimata' },
    { key: 'palletOnTime', label: 'In Orario' },
    { key: 'palletCurrentStatus', label: 'Stato', sortKey: 'palletCurrentStatus' },
    { key: 'warehouse', label: 'Magazzino' },
    { key: 'carrier', label: 'Vettore' },
    { key: 'regionName', label: 'Regione', sortKey: 'regionName' },
    { key: 'palletShipmentCode', label: 'Codice Spedizione' }
  ];

  /** Ordine corrente delle colonne (persistito) */
  columnOrder: string[] = this.columnOptions.map(c => c.key);
  /** Colonne nell'ordine corrente (per il dropdown) */
  orderedColumnOptions: ReturnsColumn[] = [];
  /** Colonne visibili nell'ordine corrente (per il rendering della tabella) */
  visibleColumns: ReturnsColumn[] = [];

  // Drag & drop delle intestazioni di colonna
  draggedColumnKey: string | null = null;
  dragOverColumnKey: string | null = null;

  /** Cella appena copiata ("palletId:colKey"), per la spunta di conferma sul bottone */
  copiedCellKey: string | null = null;
  private copiedCellTimeout: ReturnType<typeof setTimeout> | null = null;

  filteredCurrentStatus: any[] = [];
  /** Codici degli stati correnti selezionati (item_id come stringa) */
  selectedCurrentStatus: string[] = [];

  selectedOprPallet: ViewOprPallets | null = null;
  showModal: boolean = false;

  showNotification = false;
  notificationMessage = '';

  filteredWarehouses: Warehouse[] = [];
  warehouseCode: string = '';

  selectedDealerCode: string = '';
  filteredDealers: { dealerCode: string, dealerName: string, dealerOperatingAddressCity: string, count: number }[] = [];

  selectedCarrierCode: string = '';
  filteredCarriers: Carrier[] = [];

  selectedRegionCode: string = '';
  filteredRegions: Region[] = [];

  palletTypesGroupCode: string = '';
  palletTypesCode: string = '';
  filteredPalletTypes: PalletTypes[] = [];
  uniquePalletTypesGroup: PalletTypes[] = [];
  filteredNamesByGroup: PalletTypes[] = [];

  openedPalletId: number | null = null;

  sortState = { active: '', direction: '' };

  filteredBrands: Brand[] = [];
  /** Codici dei marchi selezionati */
  selectedBrands: string[] = [];
  filteredClients: Client[] = [];
  /** Codici dei clienti selezionati */
  selectedClients: string[] = [];
  clientsBrands: ClientsBrands[] = [];

  /**
   * Opzioni dei select con ricerca (app-searchable-select, stesso componente
   * usato dai filtri di shipment-management). Ricostruite da
   * `rebuildSelectOptions()` ogni volta che cambiano le liste di partenza.
   */
  clientSelectOptions: SearchableSelectOption[] = [];
  brandSelectOptions: SearchableSelectOption[] = [];
  currentStatusSelectOptions: SearchableSelectOption[] = [];
  dealerSelectOptions: SearchableSelectOption[] = [];
  carrierSelectOptions: SearchableSelectOption[] = [];
  warehouseSelectOptions: SearchableSelectOption[] = [];
  regionSelectOptions: SearchableSelectOption[] = [];
  palletTypesGroupOptions: SearchableSelectOption[] = [];
  palletTypesOptions: SearchableSelectOption[] = [];

  /** Puntualità: valori fissi, l'opzione "tutti" la fornisce il select stesso */
  readonly onTimeOptions: SearchableSelectOption[] = [
    { value: 'NOT_ON_TIME', label: 'Solo in ritardo' },
    { value: 'ON_TIME', label: 'Solo in orario' }
  ];

  /** Riallinea le opzioni dei select alle liste filtrate correnti. */
  private rebuildSelectOptions(): void {
    this.clientSelectOptions = this.filteredClients.map(c => ({
      value: c.clientCode,
      label: c.clientName || c.clientCode
    }));
    this.brandSelectOptions = this.filteredBrands.map(b => ({
      value: b.brandCode,
      label: b.brandName || b.brandCode
    }));
    this.currentStatusSelectOptions = this.filteredCurrentStatus.map(s => ({
      value: String(s.item_id),
      label: s.item_text
    }));
    this.dealerSelectOptions = this.filteredDealers.map(d => ({
      value: d.dealerCode,
      label: `${d.dealerName} - ${d.dealerOperatingAddressCity} (${d.count})`
    }));
    this.carrierSelectOptions = this.filteredCarriers.map(c => ({
      value: c.carrierCode,
      label: c.carrierName || c.carrierCode
    }));
    this.warehouseSelectOptions = this.filteredWarehouses.map(w => ({
      value: w.warehouseCode,
      label: w.warehouseName || w.warehouseCode
    }));
    this.regionSelectOptions = this.filteredRegions.map(r => ({
      value: r.regionCode,
      label: r.regionName || r.regionCode
    }));
    this.palletTypesGroupOptions = this.uniquePalletTypesGroup.map(g => ({
      value: g.palletTypesGroupCode,
      label: g.palletTypesGroupName
    }));
    this.palletTypesOptions = this.filteredNamesByGroup.map(t => ({
      value: t.palletTypesCode,
      label: `${t.palletTypesCode} - ${t.palletTypeName}`
    }));
  }

  constructor(private http: HttpClient,
              private returnRequestService: ReturnRequestService,
              private tourGuideService: TourGuideService) {}

  /** Avvia il tour guidato della pagina (stesso meccanismo di shipment-management) */
  startTour(): void {
    // Esporre il componente al window per consentire al tour di accedere ai metodi
    (window as any).returnsComponent = this;
    this.tourGuideService.startTour(RETURNS_TOUR_CONFIG);
  }

  ngOnInit(): void {

    this.loadUserPreferences();

    const stored = localStorage.getItem('userFunctions');
    if(stored) {
      const parsed = JSON.parse(stored);
      this.clientsBrands = parsed.clients; 

      this.filteredClients = this.clientsBrands.map(cb => ({
        clientCode: cb.clientCode,
        clientName: cb.clientName,
      }))
      .sort((a, b) => {
        if (!a.clientName && !b.clientName) return 0;       
        if (!a.clientName) return 1;                        
        if (!b.clientName) return -1;                       
        return a.clientName.localeCompare(b.clientName);   
      });

      const allBrands: Brand[] = this.clientsBrands.flatMap(cb =>
        cb.brands.map(b => ({
          brandCode: b.brandCode,
          brandName: b.brandName,
        }))
      ).sort((a, b) => {
        if (!a.brandName && !b.brandName) return 0;       
        if (!a.brandName) return 1;                        
        if (!b.brandName) return -1;                       
        return a.brandName.localeCompare(b.brandName);   
      });

      this.filteredBrands = Array.from(
        new Map(allBrands.map(b => [b.brandCode, b])).values()
      );

    }
    const savedPage = localStorage.getItem('currentParcelPage');
    this.currentPage = savedPage ? +savedPage : 1;

    // Recupera anche itemsPerPage dal localStorage
    const savedItemsPerPage = localStorage.getItem('itemsPerPage');
    this.itemsPerPage = savedItemsPerPage ? +savedItemsPerPage : 25;

    this.oprPallets = [];
    this.filteredOprPallets = [];

    // popolo la lista stati
    this.filteredCurrentStatus = [
      { item_id: 0, item_text: 'Pianificati (stato 0)' },
      { item_id: 1, item_text: 'Affidati (stato 1)' },
      { item_id: 2, item_text: 'Ritirati dall’autista (stato 2)' },
      { item_id: 3, item_text: 'Entrati in magazzino (stato 3)' },
      { item_id: 4, item_text: 'Usciti dal magazzino (stato 4)' },
      { item_id: 5, item_text: 'Consegnati (stato 5)' },
      { item_id: 6, item_text: 'Chiusi forzatamente (stato 6)' }
    ];

    this.rebuildSelectOptions();

    this.loadWarehouses();
    this.loadCarriers();
    this.loadRegions();
    this.loadPalletTypes();
    this.loadData();
  }

  
  onClientsChange(): void {
    this.getIntersectionClientsBrands();
  }

  getIntersectionClientsBrands() : void {
    if (!this.selectedClients || this.selectedClients.length === 0) {
      // Nessun client selezionato → mostro tutti i brands
      this.filteredBrands = Array.from(
        new Map(
          this.clientsBrands
            .flatMap(cb => cb.brands)
            .map(b => [b.brandCode, b])
        ).values()
      );
      this.selectedBrands = []; // reset
      this.rebuildSelectOptions();
      return;
    }

    // ottengo i brands per ogni client selezionato
    const brandLists = this.selectedClients.map(code => {
      const client = this.clientsBrands.find(cb => cb.clientCode === code);
      return client ? client.brands : [];
    });


    // calcolo l’intersezione tra tutte le liste
    const intersection = brandLists.reduce((acc, curr) => {
      return acc.filter(b =>
        curr.some(x => x.brandCode === b.brandCode)
      );
    });


    // aggiorno i brands disponibili
    this.filteredBrands = intersection;
    // Le selezioni non più disponibili dopo il restringimento vanno scartate,
    // altrimenti resterebbero attive senza comparire fra le opzioni
    this.selectedBrands = this.selectedBrands.filter(code =>
      intersection.some(b => b.brandCode === code)
    );
    this.rebuildSelectOptions();
  }

  // esempio di handler (opzionale)
  onStatusSelect(event: any) {
    console.log('Selezionato: ', event, this.selectedCurrentStatus);
  }

  onStatusDeSelect(event: any) {
    console.log('Deselezionato: ', event, this.selectedCurrentStatus);
  }

  onSelectAll(event: any) {
    console.log('Selezionati tutti: ', event);
  }

  onDeSelectAll(event: any) {
    console.log('Deselezionati tutti: ', event);
  }

  /**
   * Stati del reso nell'ordine di visualizzazione dello spoiler:
   * - 0, 1, 2 per numero crescente;
   * - 3 e 4 (entrata/uscita magazzino) raggruppati per codice magazzino:
   *   dentro il gruppo per data e poi per numero, i gruppi in ordine della
   *   loro prima data. Es.: 3(WHS002 01/01) 4(WHS002 03/01) 3(WHS001 01/03) 4(WHS001 01/03)
   * - 5, 6 per numero crescente.
   * Lavora su una copia: la lista ricevuta dal backend non viene toccata.
   */
  getFilteredPallets(p: ViewOprPallets): ViewComPallets[] {
    const states = (p.comPallets ?? [])
      .filter(c => c.palletComplementaryValueNumber != null && c.palletComplementaryValueNumber !== '');

    const byNumber = (a: ViewComPallets, b: ViewComPallets) =>
      Number(a.palletComplementaryValueNumber) - Number(b.palletComplementaryValueNumber);
    const isWarehouseState = (c: ViewComPallets) => {
      const n = Number(c.palletComplementaryValueNumber);
      return n === 3 || n === 4;
    };

    const before = states.filter(c => !isWarehouseState(c) && Number(c.palletComplementaryValueNumber) < 3).sort(byNumber);
    const after = states.filter(c => !isWarehouseState(c) && Number(c.palletComplementaryValueNumber) > 4).sort(byNumber);

    return [...before, ...this.sortWarehouseStates(states.filter(isWarehouseState)), ...after];
  }

  /**
   * Stati 3 e 4 raggruppati per magazzino. Ogni gruppo è ordinato per data
   * (a parità di data per numero); i gruppi si susseguono in base alla data
   * più vecchia che contengono. Le date non valide finiscono in coda.
   */
  private sortWarehouseStates(states: ViewComPallets[]): ViewComPallets[] {
    const time = (c: ViewComPallets): number => {
      const t = new Date(c.palletComplementaryValueDate).getTime();
      return isNaN(t) ? Number.POSITIVE_INFINITY : t;
    };
    const byDateThenNumber = (a: ViewComPallets, b: ViewComPallets) =>
      (time(a) - time(b)) || (Number(a.palletComplementaryValueNumber) - Number(b.palletComplementaryValueNumber));

    const groups = new Map<string, ViewComPallets[]>();
    states.forEach(c => {
      const key = c.palletComplementaryWarehouseCode ?? '';
      const group = groups.get(key);
      if (group) {
        group.push(c);
      } else {
        groups.set(key, [c]);
      }
    });

    return Array.from(groups.values())
      .map(group => group.sort(byDateThenNumber))
      .sort((a, b) => time(a[0]) - time(b[0]))
      .flat();
  }

  // True se la data della riga non è in ordine cronologico crescente
  // rispetto alla riga precedente (stati ordinati per numero).
  isDateOutOfOrder(p: ViewOprPallets, index: number): boolean {
    if (index <= 0) {
      return false;
    }
    const list = this.getFilteredPallets(p);
    const prev = list[index - 1];
    const curr = list[index];
    if (!prev || !curr) {
      return false;
    }
    const prevDate = new Date(prev.palletComplementaryValueDate).getTime();
    const currDate = new Date(curr.palletComplementaryValueDate).getTime();
    if (isNaN(prevDate) || isNaN(currDate)) {
      return false;
    }
    return currDate < prevDate;
  }

  loadCarriers(): void {
    this.http.get<Carrier[]>(`${environment.apiUrl}/tracking/carriers`).subscribe({
      next: (data) => {
        this.filteredCarriers = data;
        this.rebuildSelectOptions();
      },
      error: () => console.error('Errore nel caricamento dei carriers')
    });
  }

  loadWarehouses(): void {
    this.http.get<Warehouse[]>(`${environment.apiUrl}/tracking/warehouses`).subscribe({
      next: (data) => {
        this.filteredWarehouses = data;
        this.rebuildSelectOptions();
      },
      error: () => console.error('Errore nel caricamento dei warehouse')
    });
  }

  loadPalletTypes(): void {
    this.http.get<PalletTypes[]>(`${environment.apiUrl}/pallets/types`).subscribe({
      next: (data) => {
        console.log("palletTypes", data);
        this.uniquePalletTypesGroup = data.filter(
          (p, index, self) =>
            index === self.findIndex(other => other.palletTypesGroupCode === p.palletTypesGroupCode)
        );
        this.filteredPalletTypes = data;
        this.rebuildSelectOptions();
      },
      error: () => console.error('Errore nel caricamento delle tipologie di reso')
    });
  }

  onItemsPerPageChange(): void {
    this.goToFirstPage(); // Reset alla prima pagina quando cambi il numero di elementi
    localStorage.setItem('itemsPerPage', this.itemsPerPage.toString());
  }

  /**
   * Torna alla prima pagina persistendola: il valore in localStorage viene
   * riletto al prossimo ingresso nella pagina e resterebbe altrimenti quello
   * della ricerca precedente.
   */
  private goToFirstPage(): void {
    this.currentPage = 1;
    localStorage.setItem('currentParcelPage', '1');
  }

  // =========================================================================
  // STRUMENTARIO
  // =========================================================================

  /** Strumentario: ricarica i dati con i filtri correnti */
  refreshData(): void {
    if (this.isLoading) {
      return;
    }
    this.loadData();
  }

  /** Strumentario / header filtri: comprime o espande la sezione filtri */
  toggleFiltersSection(): void {
    this.showFilters = !this.showFilters;
    if (!this.showFilters) {
      // Gli input data vengono rimossi dal DOM con la sezione: le istanze
      // flatpickr vanno distrutte, altrimenti alla riapertura resterebbero
      // agganciate a elementi che non esistono più. I valori restano nei
      // campi del componente e vengono ripassati alla ricreazione.
      this.destroyDatePickers();
    }
    this.saveUserPreferences();
  }

  /** Strumentario: toggle densità compatta della tabella resi */
  toggleCompactTable(): void {
    this.isCompactTable = !this.isCompactTable;
    this.saveUserPreferences();
  }

  /** Strumentario: apre/chiude il dropdown delle colonne visibili */
  toggleColumnsDropdown(): void {
    // Nascondi il tooltip prima di aprire il dropdown
    this.hideTooltips();
    this.isColumnsDropdownOpen = !this.isColumnsDropdownOpen;
  }

  /** Strumentario: true se la colonna è visibile */
  isColumnVisible(key: string): boolean {
    return !this.hiddenColumns.has(key);
  }

  /** Strumentario: mostra/nasconde una colonna della tabella */
  toggleColumnVisibility(key: string): void {
    if (this.hiddenColumns.has(key)) {
      this.hiddenColumns.delete(key);
    } else {
      this.hiddenColumns.add(key);
    }
    this.refreshColumnViews();
    this.saveUserPreferences();
  }

  /** Strumentario: ripristina visibilità e ordine originali delle colonne */
  resetColumnVisibility(): void {
    this.hiddenColumns.clear();
    this.columnOrder = this.columnOptions.map(c => c.key);
    this.refreshColumnViews();
    this.saveUserPreferences();
  }

  /** True se visibilità o ordine differiscono dai default (abilita "Ripristina") */
  get hasCustomColumns(): boolean {
    return this.hiddenColumns.size > 0
      || this.columnOrder.some((key, i) => key !== this.columnOptions[i]?.key);
  }

  /** Sposta la colonna di una posizione su (-1) o giù (+1) nell'ordine corrente */
  moveColumn(key: string, delta: number): void {
    const from = this.columnOrder.indexOf(key);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= this.columnOrder.length) {
      return;
    }
    this.columnOrder.splice(from, 1);
    this.columnOrder.splice(to, 0, key);
    this.refreshColumnViews();
    this.saveUserPreferences();
  }

  // Drag & drop delle intestazioni: la colonna trascinata viene reinserita
  // nella posizione della colonna su cui viene rilasciata.
  onColumnDragStart(key: string): void {
    this.draggedColumnKey = key;
  }

  onColumnDragOver(event: DragEvent, key: string): void {
    if (!this.draggedColumnKey || this.draggedColumnKey === key) {
      return;
    }
    // Senza preventDefault il browser non considera l'elemento una drop zone
    event.preventDefault();
    this.dragOverColumnKey = key;
  }

  onColumnDrop(key: string): void {
    if (this.draggedColumnKey && this.draggedColumnKey !== key) {
      const from = this.columnOrder.indexOf(this.draggedColumnKey);
      const to = this.columnOrder.indexOf(key);
      if (from >= 0 && to >= 0) {
        this.columnOrder.splice(from, 1);
        this.columnOrder.splice(to, 0, this.draggedColumnKey);
        this.refreshColumnViews();
        this.saveUserPreferences();
      }
    }
    this.onColumnDragEnd();
  }

  onColumnDragEnd(): void {
    this.draggedColumnKey = null;
    this.dragOverColumnKey = null;
  }

  /** Colspan della riga di dettaglio: tutte le colonne visibili */
  get detailColspan(): number {
    return this.visibleColumns.length;
  }

  // ===== Copia del dato di cella =====

  /** Testo mostrato nella cella (stessa formattazione del template), per la copia */
  getCellText(p: ViewOprPallets, key: string): string {
    switch (key) {
      case 'palletClientName': return p.palletClientName || '';
      case 'palletBrandName': return p.palletBrandName || '';
      case 'palletCode': return p.palletCode || '';
      case 'palletTypesGroupName': return p.palletTypesGroupName || '';
      case 'palletWaybillCode': return p.palletWaybillCode || '';
      case 'palletDate': return this.formatDate(p.palletDate);
      case 'palletDeliveryType': return p.palletDeliveryType || '';
      case 'palletDealerCode': return p.palletDealerCode || '';
      case 'dealerName': return p.dealerName || '';
      case 'dealerAddress': {
        // Solo le parti valorizzate: "via - città" con entrambe, niente "-" orfano
        const parts = [p.dealerOperatingAddressStreet, p.dealerOperatingAddressCity]
          .map(v => (v || '').trim())
          .filter(v => !!v);
        return parts.join(' - ');
      }
      case 'palletActualDeliveryDate': return p.palletStatus === 'DELIVERED' ? this.formatDate(p.palletActualDeliveryDate) : '';
      case 'palletEstimatedDeliveryDate': return this.formatDate(p.palletEstimatedDeliveryDate);
      case 'palletOnTime': return this.descrOnTime(p.palletOnTime);
      case 'palletCurrentStatus': return p.palletCurrentStatus || '';
      case 'warehouse': return this.getFirstWarehouseName(p);
      case 'carrier': return this.getFirstCarrierName(p);
      case 'regionName': return p.regionName || '';
      case 'palletShipmentCode': return p.palletShipmentCode || '';
      default: return '';
    }
  }

  /** True se la cella è quella appena copiata (il bottone mostra la spunta) */
  isCellCopied(p: ViewOprPallets, key: string): boolean {
    return this.copiedCellKey === `${p.palletId}:${key}`;
  }

  /** Copia negli appunti il dato della cella, con spunta di conferma temporanea */
  copyCellValue(p: ViewOprPallets, key: string, event: Event): void {
    // Il click sulla riga apre lo spoiler degli stati: non va propagato
    event.stopPropagation();
    const text = this.getCellText(p, key);
    if (!text) {
      return;
    }
    this.writeToClipboard(text).then(ok => {
      if (!ok) {
        return;
      }
      this.copiedCellKey = `${p.palletId}:${key}`;
      if (this.copiedCellTimeout) {
        clearTimeout(this.copiedCellTimeout);
      }
      this.copiedCellTimeout = setTimeout(() => this.copiedCellKey = null, 1500);
    });
  }

  private writeToClipboard(text: string): Promise<boolean> {
    if (navigator.clipboard?.writeText) {
      return navigator.clipboard.writeText(text).then(() => true).catch(() => this.legacyCopy(text));
    }
    return Promise.resolve(this.legacyCopy(text));
  }

  /** Fallback per contesti senza Clipboard API (es. origine http non sicura) */
  private legacyCopy(text: string): boolean {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch {
      ok = false;
    }
    document.body.removeChild(textarea);
    return ok;
  }

  /**
   * Riallinea gli elenchi renderizzati (dropdown e tabella) all'ordine e alla
   * visibilità correnti. Campi precalcolati e non getter: i getter creerebbero
   * un array nuovo a ogni change detection, rirenderizzando la tabella.
   */
  private refreshColumnViews(): void {
    const byKey = new Map(this.columnOptions.map(c => [c.key, c]));
    this.orderedColumnOptions = this.columnOrder
      .map(key => byKey.get(key))
      .filter((c): c is ReturnsColumn => !!c);
    this.visibleColumns = this.orderedColumnOptions.filter(c => this.isColumnVisible(c.key));
  }

  /** Carica le preferenze dello strumentario da localStorage */
  private loadUserPreferences(): void {
    try {
      const savedSettings = localStorage.getItem(this.STORAGE_KEY);
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);

        if (typeof settings.showFilters === 'boolean') {
          this.showFilters = settings.showFilters;
        }

        if (typeof settings.isCompactTable === 'boolean') {
          this.isCompactTable = settings.isCompactTable;
        }

        if (Array.isArray(settings.hiddenColumns)) {
          this.hiddenColumns = new Set<string>(settings.hiddenColumns);
        }

        if (Array.isArray(settings.columnOrder)) {
          // L'ordine salvato può riferirsi a colonne rinominate o rimosse:
          // si tengono solo le chiavi note; le colonne nuove (assenti nel
          // salvataggio) vengono inserite nella loro posizione di default
          const known = this.columnOptions.map(c => c.key);
          const saved = (settings.columnOrder as string[]).filter(key => known.includes(key));
          known.forEach((key, defaultIndex) => {
            if (!saved.includes(key)) {
              saved.splice(Math.min(defaultIndex, saved.length), 0, key);
            }
          });
          this.columnOrder = saved;
        }
      }
    } catch (error) {
      console.warn('Error loading returns preferences:', error);
    }
    this.refreshColumnViews();
  }

  /** Salva le preferenze dello strumentario su localStorage */
  private saveUserPreferences(): void {
    try {
      const settings = {
        showFilters: this.showFilters,
        isCompactTable: this.isCompactTable,
        hiddenColumns: Array.from(this.hiddenColumns),
        columnOrder: this.columnOrder
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      console.warn('Error saving returns preferences:', error);
    }
  }

  /** Nasconde i tooltip Bootstrap attivi (es. prima di aprire un dropdown) */
  private hideTooltips(): void {
    if (typeof document !== 'undefined' && typeof window !== 'undefined' && (window as any).bootstrap) {
      const tooltipElements = document.querySelectorAll('[data-bs-toggle="tooltip"]');
      tooltipElements.forEach(element => {
        try {
          const tooltipInstance = (window as any).bootstrap.Tooltip.getInstance(element);
          tooltipInstance?.hide();
        } catch (error) {
          console.error('Error hiding tooltip:', error);
        }
      });
    }
  }

  // ===== Popup di chiusura resi da Excel =====
  showCloseReturnsPopup = false;

  openCloseReturnsPopup(): void {
    this.showCloseReturnsPopup = true;
  }

  closeCloseReturnsPopup(): void {
    this.showCloseReturnsPopup = false;
  }

  /**
   * Esito dell'importazione dell'Excel di chiusura, già dettagliato nel popup.
   * L'endpoint chiude i resi a database, quindi la pagina va riletta: gli stati
   * a video sono cambiati.
   */
  onCloseReturnsImported(result: ReturnsCsvImportResponse): void {
    if (result.appliedRows > 0) {
      this.loadData();
    }
  }

  // ===== Selezione righe per la chiusura multipla =====
  /**
   * Resi selezionati con la checkbox di riga, per palletId: sopravvive al
   * cambio pagina e al riordino, si svuota dopo una chiusura riuscita e al
   * reset dei filtri.
   */
  selectedPallets = new Map<number, ViewOprPallets>();

  /** Un reso è chiudibile manualmente finché non è consegnato */
  isPalletClosable(p: ViewOprPallets): boolean {
    return p.palletStatus !== 'DELIVERED';
  }

  isPalletSelected(p: ViewOprPallets): boolean {
    return this.selectedPallets.has(p.palletId);
  }

  togglePalletSelection(p: ViewOprPallets, event: Event): void {
    // Il click sulla riga apre lo spoiler degli stati: la checkbox non deve propagarlo
    event.stopPropagation();
    if (this.selectedPallets.has(p.palletId)) {
      this.selectedPallets.delete(p.palletId);
    } else if (this.isPalletClosable(p)) {
      this.selectedPallets.set(p.palletId, p);
    }
  }

  /** Resi chiudibili della pagina corrente */
  get closablePalletsOnPage(): ViewOprPallets[] {
    return this.filteredOprPallets.filter(p => this.isPalletClosable(p));
  }

  /** True se tutti i resi chiudibili della pagina sono selezionati */
  get areAllOnPageSelected(): boolean {
    const closable = this.closablePalletsOnPage;
    return closable.length > 0 && closable.every(p => this.selectedPallets.has(p.palletId));
  }

  /** True se solo una parte dei resi chiudibili della pagina è selezionata */
  get isPageSelectionPartial(): boolean {
    const closable = this.closablePalletsOnPage;
    const selectedCount = closable.filter(p => this.selectedPallets.has(p.palletId)).length;
    return selectedCount > 0 && selectedCount < closable.length;
  }

  /** Seleziona o deseleziona tutti i resi chiudibili della pagina corrente */
  toggleSelectAllOnPage(event: Event): void {
    event.stopPropagation();
    const closable = this.closablePalletsOnPage;
    if (this.areAllOnPageSelected) {
      closable.forEach(p => this.selectedPallets.delete(p.palletId));
    } else {
      closable.forEach(p => this.selectedPallets.set(p.palletId, p));
    }
  }

  get selectedCount(): number {
    return this.selectedPallets.size;
  }

  clearSelection(): void {
    this.selectedPallets.clear();
  }

  // ===== Popup di chiusura manuale reso (data di consegna) =====
  showCloseManualPopup = false;
  /** Resi passati al popup: uno dal bottone di riga, più di uno dalla selezione */
  closeManualPallets: ViewOprPallets[] = [];

  openCloseManualPopup(p: ViewOprPallets, event: Event): void {
    // Il click sulla riga apre lo spoiler degli stati: il bottone non deve propagarlo
    event.stopPropagation();
    this.closeManualPallets = [p];
    this.showCloseManualPopup = true;
  }

  /**
   * Apre il popup con tutti i resi selezionati, da chiudere con la stessa data.
   * Il bottone resta sempre attivo: senza selezione il popup mostra la guida
   * alla chiusura massiva al posto del form.
   */
  openCloseSelectedPopup(): void {
    this.closeManualPallets = Array.from(this.selectedPallets.values());
    this.showCloseManualPopup = true;
  }

  closeCloseManualPopup(): void {
    this.showCloseManualPopup = false;
    this.closeManualPallets = [];
  }

  // ===== Popup Resi per vettore (intervallo di date + vettori) =====
  showPlatformExcelPopup = false;
  /** Ultima selezione con cui il popup ha generato e scaricato il report */
  platformExcelSelection: PlatformExcelSelection | null = null;

  openPlatformExcelPopup(): void {
    this.showPlatformExcelPopup = true;
  }

  closePlatformExcelPopup(): void {
    this.showPlatformExcelPopup = false;
  }

  /**
   * Il popup ha generato e scaricato il report (GET /pallets/returns-report):
   * si conserva la selezione usata, utile per riproporla in futuro.
   */
  onPlatformExcelSaved(selection: PlatformExcelSelection): void {
    this.platformExcelSelection = selection;
  }

  /**
   * Il backend ha registrato il quinto stato e la data di consegna: la pagina
   * va riletta perché stato e data della riga a video sono cambiati.
   */
  onPalletManuallyClosed(_updated: any): void {
    this.loadData();
  }

  /**
   * Chiusura multipla completata: la selezione viene svuotata (i resi saltati
   * o scartati restano leggibili nell'esito del popup) e la pagina riletta.
   */
  onPalletsBulkClosed(_result: PalletCloseBulkResponse): void {
    this.clearSelection();
    this.loadData();
  }

  // ===== Popup di stampa reso (label / bolla) =====
  showReturnPrintPopup = false;
  printPopupRequest: ViewOprPallets | null = null;
  /** Resi della stessa bolla del reso selezionato, elencati nel popup. */
  printPopupPallets: Pallets[] = [];
  /** Anagrafica del dealer della riga selezionata (per i dati della label). */
  printPopupDealer: Dealer | null = null;
  /** Cache delle anagrafiche dealer già richieste, per codice. */
  private dealersByCode: Record<string, Dealer> = {};

  openReturnPrintPopup(p: ViewOprPallets, event: Event): void {
    // Il click sulla riga apre lo spoiler degli stati: il bottone non deve propagarlo
    event.stopPropagation();
    this.printPopupRequest = p;
    // Si stampa solo la label della riga scelta: il popup elenca il solo reso
    // cliccato, non gli altri resi della stessa bolla.
    this.printPopupPallets = [p];
    this.printPopupDealer = null;
    this.loadPrintPopupDealer(p.palletDealerCode);
    this.showReturnPrintPopup = true;
  }

  closeReturnPrintPopup(): void {
    this.showReturnPrintPopup = false;
    this.printPopupRequest = null;
    this.printPopupPallets = [];
    this.printPopupDealer = null;
  }

  /**
   * Anagrafica completa del dealer per i dati della label: le righe della
   * tabella ne hanno solo un estratto, il popup la richiede al backend come
   * fa return-request (con cache per non rifare la chiamata a ogni apertura).
   */
  private loadPrintPopupDealer(dealerCode: string): void {
    const code = (dealerCode || '').trim();
    if (!code) {
      return;
    }
    const cached = this.dealersByCode[code];
    if (cached) {
      this.printPopupDealer = cached;
      return;
    }
    this.returnRequestService.getDealersByCodes([code], this.clientsBrands).subscribe({
      next: (dealers: Dealer[]) => {
        const dealer = (dealers || []).find(d => d.dealerCode === code) || dealers?.[0] || null;
        if (dealer) {
          this.dealersByCode[code] = dealer;
        }
        // Nel frattempo il popup può essere stato chiuso o riaperto su un'altra riga
        if (this.printPopupRequest?.palletDealerCode === code) {
          this.printPopupDealer = dealer;
        }
      },
      error: (err) => console.error('Errore caricamento anagrafica dealer per la stampa:', err)
    });
  }

  /** Selettore elementi per pagina dell'header della sezione Resi */
  setItemsPerPage(size: number): void {
    if (!this.pageSizeOptions.includes(size)) {
      return;
    }
    this.itemsPerPage = size;
    this.onItemsPerPageChange();
    this.loadData();
  }

  /** True se almeno un filtro è valorizzato (badge "filtri attivi"). */
  hasActiveFilters(): boolean {
    return !!(this.searchDealerCode || this.selectedDealerCode || this.searchPalletCode
      || this.searchWaybillCode || this.onTimeStatus || this.selectedCarrierCode
      || this.warehouseCode || this.selectedRegionCode || this.palletTypesGroupCode
      || this.palletTypesCode || this.palletStatus || this.filterForcedDelivery
      || this.dateStart || this.dateEnd || this.dateDeliveryStart || this.dateDeliveryEnd
      || this.selectedCurrentStatus.length > 0
      || this.selectedBrands.length > 0 || this.selectedClients.length > 0);
  }

  resetItemsPerPage(): void {
    this.itemsPerPage = 25;
    this.currentPage = 1;
    localStorage.setItem('itemsPerPage', '25');
  }

  // Vai a pagina
  isPageJumpOpen = false;
  pageJumpValue = '';
  @ViewChild('pageJumpInput') pageJumpInput?: ElementRef<HTMLInputElement>;
  // Vai all'elemento
  isItemJumpOpen = false;
  itemJumpValue = '';
  @ViewChild('itemJumpInput') itemJumpInput?: ElementRef<HTMLInputElement>;

  openItemJump(): void {
    this.itemJumpValue = '';
    this.isItemJumpOpen = true;
    setTimeout(() => this.itemJumpInput?.nativeElement.focus());
  }

  closeItemJump(): void {
    this.isItemJumpOpen = false;
  }

  /** Va alla pagina che contiene l'elemento con il numero digitato (limitato tra 1 e il totale) */
  applyItemJump(): void {
    const n = parseInt(this.itemJumpValue, 10);
    this.closeItemJump();
    if (!isNaN(n)) {
      const item = Math.min(Math.max(n, 1), Math.max(1, this.totalCount));
      this.onPageChange(Math.ceil(item / this.itemsPerPage));
    }
  }


  openPageJump(): void {
    this.pageJumpValue = '';
    this.isPageJumpOpen = true;
    setTimeout(() => this.pageJumpInput?.nativeElement.focus());
  }

  closePageJump(): void {
    this.isPageJumpOpen = false;
  }

  /** Va alla pagina digitata (limitata tra 1 e l'ultima) */
  applyPageJump(): void {
    const n = parseInt(this.pageJumpValue, 10);
    this.closePageJump();
    if (!isNaN(n)) {
      this.onPageChange(Math.min(Math.max(n, 1), this.getTotalPages()));
    }
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.getTotalPages() || page === this.currentPage) {
      return;
    }
    this.currentPage = page;
    localStorage.setItem('currentParcelPage', page.toString());
    // La nuova pagina va richiesta al backend
    this.loadData();
  }

  getTotalPages(): number {
    return Math.max(1, Math.ceil(this.totalCount / this.itemsPerPage));
  }

  /** Numeri di pagina mostrati nella barra (finestra di 5 attorno alla corrente) */
  getPageNumbers(): number[] {
    const totalPages = this.getTotalPages();
    const maxButtons = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxButtons / 2));
    const end = Math.min(totalPages, start + maxButtons - 1);
    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }
    const pages: number[] = [];
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }
  /** Numeri di pagina con le pagine estreme e i puntini (null) quando ce ne sono altre oltre la finestra */
  getPageItems(): (number | null)[] {
    const window = this.getPageNumbers();
    const total = this.getTotalPages();
    if (window.length === 0) { return []; }
    const items: (number | null)[] = [];
    const first = window[0];
    const last = window[window.length - 1];
    if (first > 1) {
      items.push(1);
      if (first > 2) { items.push(null); }
    }
    items.push(...window);
    if (last < total) {
      if (last < total - 1) { items.push(null); }
      items.push(total);
    }
    return items;
  }

  trackByPageItem(index: number, item: number | null): number {
    return item ?? -index - 1;
  }

  trackByPageNumber(index: number, page: number): number {
    return page;
  }

  /** Primo elemento mostrato nella pagina corrente (0 a lista vuota) */
  get rangeStart(): number {
    return this.totalCount === 0 ? 0 : (this.currentPage - 1) * this.itemsPerPage + 1;
  }

  /** Ultimo elemento mostrato nella pagina corrente */
  get rangeEnd(): number {
    return Math.min(this.currentPage * this.itemsPerPage, this.totalCount);
  }

  /** Invio nei campi di testo: stesso comportamento del pulsante Applica */
  onSearchEnter(): void {
    if (this.isLoading) {
      return;
    }
    this.applyFilters();
  }

  private destroyDatePickers(): void {
    this.datePickers.forEach(picker => picker.destroy());
    this.datePickers.clear();
  }

  applyFilters(): void {
    this.goToFirstPage();
    this.clearDateError();

    const creationStartSet = !!this.dateStart;
    const creationEndSet = !!this.dateEnd;
    const deliveryStartSet = !!this.dateDeliveryStart;
    const deliveryEndSet = !!this.dateDeliveryEnd;

    const creationDatesValid = creationStartSet && creationEndSet;
    const deliveryDatesValid = deliveryStartSet && deliveryEndSet;

    // Le date sono facoltative: si valida solo che un intervallo iniziato sia
    // completo e coerente, non che ce ne sia almeno uno.
    const affidoPartial = creationStartSet !== creationEndSet;
    const deliveryPartial = deliveryStartSet !== deliveryEndSet;

    if (affidoPartial && deliveryPartial) {
      this.setDateError(
        'Intervalli incompleti: per affido e consegna servono sia la data "Da" che la data "A".',
        'entrambi');
      return;
    }
    if (affidoPartial) {
      this.setDateError(
        'Intervallo di affido incompleto: compila sia "Affido Da" che "Affido A".',
        'affido');
      return;
    }
    if (deliveryPartial) {
      this.setDateError(
        'Intervallo di consegna incompleto: compila sia "Consegna Da" che "Consegna A".',
        'consegna');
      return;
    }

    // Controllo coerenza date affido
    if (creationDatesValid && new Date(this.dateStart) > new Date(this.dateEnd)) {
      this.setDateError(
        `"Affido Da" (${this.getFormattedDate('dateStart')}) è successiva ad "Affido A" (${this.getFormattedDate('dateEnd')}): correggi l'intervallo.`,
        'affido');
      return;
    }

    // Controllo coerenza date consegna
    if (deliveryDatesValid && new Date(this.dateDeliveryStart) > new Date(this.dateDeliveryEnd)) {
      this.setDateError(
        `"Consegna Da" (${this.getFormattedDate('dateDeliveryStart')}) è successiva a "Consegna A" (${this.getFormattedDate('dateDeliveryEnd')}): correggi l'intervallo.`,
        'consegna');
      return;
    }

    this.loadWarehouses();
    this.loadData();
  }

  private setDateError(message: string, scope: 'affido' | 'consegna' | 'entrambi'): void {
    this.dateError = message;
    this.dateErrorScope = scope;
  }

  /** L'errore sparisce appena l'utente rimette mano alle date */
  private clearDateError(): void {
    this.dateError = '';
    this.dateErrorScope = null;
  }

  /** True se il campo appartiene all'intervallo segnalato dall'errore corrente */
  isDateFieldInError(field: ReturnsDateField): boolean {
    if (!this.dateErrorScope) {
      return false;
    }
    const isAffido = field === 'dateStart' || field === 'dateEnd';
    return this.dateErrorScope === 'entrambi'
      || (this.dateErrorScope === 'affido' && isAffido)
      || (this.dateErrorScope === 'consegna' && !isAffido);
  }

  /** Clienti/marchi effettivi della ricerca, a partire dalle selezioni correnti */
  private buildClientsBrandsSelected(): ClientsBrands[] {
    let clientsBrandsSelected: ClientsBrands[];
    if(this.filteredClients.length == 1) {
      this.selectedClients = this.filteredClients.map(c => c.clientCode);
    }
    if (this.selectedClients.length > 0) {
      clientsBrandsSelected = this.selectedClients.map(clientCode => {
        // trovo il client originale da clientsBrands
        const originalClient = this.clientsBrands.find(cb => cb.clientCode === clientCode);

        var filteredBrandsFromClient: Brand[] = [];
        if(this.selectedBrands.length > 0) {
          // brands effettivi = intersezione tra quelli del client e quelli selezionati
          filteredBrandsFromClient = (originalClient?.brands || []).filter(b =>
            this.selectedBrands.includes(b.brandCode)
          );
        }
        else {
          filteredBrandsFromClient = this.filteredBrands;
        }

        return {
          clientCode: clientCode,
          clientName: originalClient?.clientName,
          brands: filteredBrandsFromClient
        } as ClientsBrands;
      });
    } else if (this.selectedBrands && this.selectedBrands.length > 0) {
      // nessun client selezionato, ma brand sì
      clientsBrandsSelected = this.clientsBrands
        .map(cb => {
          const matchingBrands = cb.brands.filter(b =>
            this.selectedBrands.includes(b.brandCode)
          );
          if (matchingBrands.length > 0) {
            return {
              clientCode: cb.clientCode,
              clientName: cb.clientName,
              brands: matchingBrands
            } as ClientsBrands;
          }
          return null;
        })
        .filter(cb => cb !== null) as ClientsBrands[];

    } else {
      // né client né brand selezionati → ritorno tutto
      clientsBrandsSelected = this.clientsBrands;
    }

    return clientsBrandsSelected;
  }

  /**
   * Body di POST /pallets/search: filtri, paginazione (page 1-based),
   * ordinamento e i filtri stati correnti/puntualità, che devono stare
   * nella WHERE del server per non falsare pagine e totale.
   */
  private buildSearchBody(page: number, pageSize: number): any {
    return {
      waybillCode: this.searchWaybillCode || '',
      palletCode: this.searchPalletCode || '',
      dateStart: this.dateStart,
      dateEnd: this.dateEnd,
      dealerCode: this.searchDealerCode !== '' ? this.searchDealerCode : this.selectedDealerCode,
      warehouseCode: this.warehouseCode || '',
      regionCode: this.selectedRegionCode,
      carrierCode: this.selectedCarrierCode,
      palletStatus: this.palletStatus,
      dateDeliveryStart: this.dateDeliveryStart,
      dateDeliveryEnd: this.dateDeliveryEnd,
      palletTypesGroupCode: this.palletTypesGroupCode,
      palletTypesCode: this.palletTypesCode,
      currentStatus: this.selectedCurrentStatus,
      onTime: this.onTimeStatus || '',
      // Presente solo da attivo: spento significa "nessun filtro sulla chiusura manuale"
      ...(this.filterForcedDelivery ? { deliveryDateIsForced: true } : {}),
      page: page,
      pageSize: pageSize,
      sortBy: this.sortState.active || null,
      sortDirection: this.sortState.direction ? this.sortState.direction.toUpperCase() : null,
      clientsBrands: this.buildClientsBrandsSelected()
    };
  }

  loadData(): void {
    // Una sola ricerca in volo: la precedente viene annullata, altrimenti una
    // risposta più lenta arrivata dopo sovrascriverebbe i risultati dei
    // filtri correnti.
    this.searchSubscription?.unsubscribe();

    this.isLoading = true; // mostra il loader

    this.searchSubscription = this.http.post<PalletsSearchResponse>(
      `${environment.apiUrl}/pallets/search`,
      this.buildSearchBody(this.currentPage, this.itemsPerPage)
    ).subscribe({
      next: (res) => {
        const rows = res?.pallets ?? [];

        this.oprPallets = rows.map(p => ({
          ...p,
          showComPallets: false
        }));
        this.filteredOprPallets = this.oprPallets;
        this.temp = [...this.filteredOprPallets];
        this.totalCount = res?.totalCount ?? rows.length;

        // Pagina fuori range (es. ripristinata da localStorage con meno
        // risultati di prima): riallinea all'ultima pagina e ricarica.
        const lastPage = Math.max(1, Math.ceil(this.totalCount / this.itemsPerPage));
        if (rows.length === 0 && this.totalCount > 0 && this.currentPage > lastPage) {
          this.currentPage = lastPage;
          localStorage.setItem('currentParcelPage', String(lastPage));
          this.loadData();
          return;
        }

        this.updateDealerFilterOptions(this.filteredOprPallets);

        this.isLoading = false;
      },
      error: (err) => {
        console.error('Errore durante la chiamata HTTP:', err);
        this.isLoading = false;
      }
    });
  }


  // toggleComPallets(pallet: ViewOprPallets) {
  //   if(pallet.comPallets.filter(c => c.palletComplementaryValueNumber != null && c.palletComplementaryValueNumber !== '').length > 0) {
  //     pallet.showComPallets = !pallet.showComPallets;
  //   }
  //   else {
  //     pallet.showComPallets = false;
  //   }
  // }

  toggleComPallets(p: any): void {
    if (this.openedPalletId === p.palletId) {
      this.openedPalletId = null; // chiude se clicco sulla stessa riga aperta
    } else {
      this.openedPalletId = p.palletId; // apre quella cliccata e chiude eventuale precedente
      // Inizializza i tooltip Bootstrap dopo il render dello spoiler
      setTimeout(() => this.initializeBootstrapTooltips(), 100);
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.initializeBootstrapTooltips(), 100);
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
    this.destroyDatePickers();
    if (this.copiedCellTimeout) {
      clearTimeout(this.copiedCellTimeout);
    }
  }

  // ===== Filtri data =====

  /** Input DOM del campo, per agganciarci flatpickr */
  private dateInputFor(field: ReturnsDateField): HTMLInputElement | undefined {
    switch (field) {
      case 'dateStart': return this.dateStartInput?.nativeElement;
      case 'dateEnd': return this.dateEndInput?.nativeElement;
      case 'dateDeliveryStart': return this.dateDeliveryStartInput?.nativeElement;
      case 'dateDeliveryEnd': return this.dateDeliveryEndInput?.nativeElement;
    }
  }

  /** Valore del campo mostrato nell'input readonly, in dd/MM/yyyy */
  getFormattedDate(field: ReturnsDateField): string {
    const value = this[field];
    if (!value) {
      return '';
    }
    const [year, month, day] = value.split('-');
    return day && month && year ? `${day}/${month}/${year}` : value;
  }

  /**
   * Apre il calendario del campo, creandolo alla prima richiesta.
   * Il valore resta una stringa yyyy-MM-dd: è il formato che il backend si
   * aspetta nel body e su cui lavora già il resto della pagina.
   */
  openDatePicker(field: ReturnsDateField): void {
    let picker = this.datePickers.get(field);

    if (!picker) {
      const input = this.dateInputFor(field);
      if (!input) {
        return;
      }
      picker = flatpickr(input, {
        dateFormat: 'd/m/Y',
        allowInput: false,
        clickOpens: false,
        locale: Italian,
        defaultDate: this[field] || undefined,
        onChange: (selectedDates: Date[]) => {
          this[field] = selectedDates.length ? this.toIsoDay(selectedDates[0]) : '';
          this.clearDateError();
          this.datePickers.get(field)?.close();
        },
        onClose: () => {
          this.dateInputFor(field)?.blur();
        }
      });
      this.datePickers.set(field, picker);
    }

    if (!picker.isOpen) {
      if (this[field]) {
        picker.setDate(this[field], false);
      }
      picker.open();
    }
  }

  /** Sposta il campo di N giorni; se vuoto parte da oggi */
  shiftDateField(field: ReturnsDateField, days: number): void {
    const base = this[field] ? new Date(this[field]) : new Date();
    if (this[field]) {
      base.setDate(base.getDate() + days);
    }
    this.setDateField(field, this.toIsoDay(base));
  }

  setDateFieldToday(field: ReturnsDateField): void {
    this.setDateField(field, this.toIsoDay(new Date()));
  }

  clearDateField(field: ReturnsDateField): void {
    this[field] = '';
    this.datePickers.get(field)?.clear();
    this.clearDateError();
  }

  private setDateField(field: ReturnsDateField, value: string): void {
    this[field] = value;
    this.datePickers.get(field)?.setDate(value, false);
    this.clearDateError();
  }

  /** Data in yyyy-MM-dd usando il fuso locale (toISOString sposterebbe il giorno) */
  private toIsoDay(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Inizializza i tooltip Bootstrap presenti nel DOM
   */
  private initializeBootstrapTooltips(): void {
    if (typeof document !== 'undefined' && typeof window !== 'undefined' && (window as any).bootstrap) {
      const tooltipTriggerList = Array.from(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
      tooltipTriggerList.forEach(tooltipTriggerEl => {
        try {
          // Evita doppia inizializzazione sullo stesso elemento
          if (!(window as any).bootstrap.Tooltip.getInstance(tooltipTriggerEl)) {
            new (window as any).bootstrap.Tooltip(tooltipTriggerEl);
          }
        } catch (error) {
          console.error('Error initializing tooltip:', error);
        }
      });
    }
  }

  onGlobalSearchChange() {
    const term = this.globalSearchTerm.toLowerCase();

    if (!term) {
      this.filteredOprPallets = [...this.temp];
      return;
    }

    this.filteredOprPallets = this.filteredOprPallets.filter(pallet => {
      return Object.values(pallet).some(val =>
        val != null && val.toString().toLowerCase().includes(term)
      );
    });
  }

  toDateOnly(d: string | Date): string {
    return new Date(d).toISOString().split('T')[0];
  }

  updateDealerFilterOptions(pallets: ViewOprPallets[]) {
    const dealerMap = new Map<string, { dealerName: string, dealerOperatingAddressCity: string, count: number }>();

    for (const pallet of pallets) {
      const code = pallet.palletDealerCode;
      const dealerOperatingAddressCity = pallet.dealerOperatingAddressCity;
      const name = pallet.dealerName || 'Dealer sconosciuto';

      if (!dealerMap.has(code)) {
        dealerMap.set(code, { dealerName: name, dealerOperatingAddressCity: dealerOperatingAddressCity, count: 1 });
      } else {
        dealerMap.get(code)!.count++;
      }
    }

    this.filteredDealers = Array.from(dealerMap.entries())
    .sort(([, a], [, b]) => a.dealerName.localeCompare(b.dealerName))
    .map(([dealerCode, data]) => ({
      dealerCode,
      dealerName: data.dealerName,
      dealerOperatingAddressCity: data.dealerOperatingAddressCity,
      count: data.count
    }));

    this.rebuildSelectOptions();
  }

  // updateCarrierFilterOptions(pallets: ViewOprPallets[]) {
  //   const carrierMap = new Map<string, { carrierName: string, count: number }>();

  //   for (const pallet of pallets) {
  //     for(const com of pallet.comPallets) {
  //       if(com.palletComplementaryCarrierCode != null) {
  //         const code = com.palletComplementaryCarrierCode;
  //         const name = com.carrierName || 'Carrier sconosciuto';

  //         if (!carrierMap.has(code)) {
  //           carrierMap.set(code, { carrierName: name, count: 1 });
  //         } else {
  //           carrierMap.get(code)!.count++;
  //         }
  //       }
  //     }
  //   }

  //   this.filteredCarriers = Array.from(carrierMap.entries())
  //   .sort(([, a], [, b]) => a.carrierName.localeCompare(b.carrierName))
  //   .map(([carrierCode, data]) => ({
  //     carrierCode,
  //     carrierName: data.carrierName,
  //     count: data.count
  //   }));
  // }

  /**
   * Anagrafica completa delle regioni: con la ricerca paginata i dati caricati
   * sono solo quelli della pagina corrente, quindi le opzioni del filtro non
   * possono più essere dedotte dalle righe a schermo.
   */
  loadRegions(): void {
    this.http.get<Region[]>(`${environment.apiUrl}/tracking/regions`).subscribe({
      next: (data) => {
        this.filteredRegions = [...data].sort((a, b) =>
          (a.regionName || a.regionCode).localeCompare(b.regionName || b.regionCode)
        );
        this.rebuildSelectOptions();
      },
      error: () => console.error('Errore nel caricamento delle regioni')
    });
  }

  /**
   * Righe per l'export: in memoria c'è solo la pagina corrente, quindi
   * l'intero filtrato va richiesto in un'unica chiamata (page 1 con
   * pageSize = totale).
   */
  private fetchRowsForExport(next: (rows: ViewOprPallets[]) => void): void {
    if (this.totalCount === 0) {
      return;
    }
    this.isLoading = true;
    this.http.post<PalletsSearchResponse>(
      `${environment.apiUrl}/pallets/search`,
      this.buildSearchBody(1, this.totalCount)
    ).subscribe({
      next: (res) => {
        this.isLoading = false;
        next(res?.pallets ?? []);
      },
      error: (err) => {
        console.error('Errore nel recupero dei dati per l\'export:', err);
        this.isLoading = false;
      }
    });
  }

  exportParcelsDetailsToExcel(): void {
    this.fetchRowsForExport(sourceRows => this.buildDetailsExport(sourceRows));
  }

  private buildDetailsExport(sourceRows: ViewOprPallets[]): void {
    const exportData = sourceRows.reduce((rows: any[], pallet) => {
      const coms = pallet.comPallets
        ?.filter(c => c.palletComplementaryValueNumber != null && c.palletComplementaryValueNumber !== '') 
        || [];

      const row: any = {
        'Codice Reso': pallet.palletCode,
        'Tipologia': pallet.palletTypesGroupName,
        'Codice Bolla': pallet.palletWaybillCode,
        'Contenuto': pallet.palletDeliveryType,
        'Data Affido': this.formatDate(pallet.palletDate),
        'Codice Dealer': pallet.palletDealerCode,
        'Dealer': pallet.dealerName,
        'Indirizzo Dealer': pallet.dealerOperatingAddressStreet,
        'Città Dealer': pallet.dealerOperatingAddressCity,
        'Data Consegna Effettiva': pallet.palletStatus === 'DELIVERED'
          ? this.formatDate(pallet.palletActualDeliveryDate)
          : '',
        'Data Consegna Stimata': this.formatDate(pallet.palletEstimatedDeliveryDate),
        'In Orario': this.descrOnTime(pallet.palletOnTime),
        'Regione': pallet.regionName,
        'Codice Spedizione': pallet.palletShipmentCode,
        'Tipo Consegna': pallet.palletDeliveryType,
      };

      // Stati fissi: 1, 2, 3, 4, 5
      for (let stato = 1; stato <= 5; stato++) {
        // cerco il COM con valuenumber = stato
        const com = coms.find(c => Number(c.palletComplementaryValueNumber) === stato);

        const suffix = ` Stato ${stato}`; 

        row[`Data${suffix}`] = com ? this.formatDate(com.palletComplementaryValueDate) : '';
        row[`Magazzino${suffix}`] = com ? com.warehouseCodeName : '';
        row[`Vettore${suffix}`] = com ? com.carrierName : '';
      }

      rows.push(row);
      return rows;
    }, []);

    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);
    const workbook: XLSX.WorkBook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
    const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    const blobData = new Blob([excelBuffer], { type: 'application/octet-stream' });
    const now = new Date();
    const timestamp = `${now.getFullYear()}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}${now.getSeconds().toString().padStart(2, '0')}`;

    FileSaver.saveAs(blobData, `pallets-details_${timestamp}.xlsx`);
  }

    private descrOnTime(parcelOnTime: string): string {
    switch (parcelOnTime) {
      case 'ON_TIME': return 'Si';
      case 'NOT_ON_TIME': return 'No';
      default: return parcelOnTime;
    }
  }

  exportParcelsToExcel(): void {
    this.fetchRowsForExport(sourceRows => this.buildParcelsExport(sourceRows));
  }

  private buildParcelsExport(sourceRows: ViewOprPallets[]): void {
    const exportData = sourceRows.map(p => ({
      'Codice Reso': p.palletCode,
      'Tipologia': p.palletTypesGroupName,
      'Codice Bolla': p.palletWaybillCode,
      'Data': this.formatDate(p.palletDate),
      'Contenuto': p.palletDeliveryType,
      'Codice Dealer': p.palletDealerCode,
      'Dealer': p.dealerName,
      'Indirizzo Dealer': `${p.dealerOperatingAddressStreet}`,
      'Città Dealer': `${p.dealerOperatingAddressCity}`,
      'Stato corrente': p.palletCurrentStatus,
      'Data Consegna Effettiva': p.palletStatus === 'DELIVERED' ? this.formatDate(p.palletActualDeliveryDate) : '',
      'Data Consegna Stimata': this.formatDate(p.palletEstimatedDeliveryDate),
      'In Orario': this.descrOnTime(p.palletOnTime),
      'Magazzino': this.getFirstWarehouseName(p),
      'Vettore': this.getFirstCarrierName(p),
      'Regione': p.regionName,
      'Codice Spedizione': p.palletShipmentCode
    }));

    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);
    const workbook: XLSX.WorkBook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
    const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    const blobData = new Blob([excelBuffer], { type: 'application/octet-stream' });
    const now = new Date();
    const timestamp = `${now.getFullYear()}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}${now.getSeconds().toString().padStart(2, '0')}`;

    FileSaver.saveAs(blobData, `pallets_${timestamp}.xlsx`);
  }

  // private translatePalletStatus(status: string): string {
  //   switch (status) {
  //     case 'REQUEST_RETURN': return 'Richiesta di reso';
  //     case 'PICKED_UP': return 'Preso in carico';
  //     case 'SUSPENDED': return 'Sospeso';
  //     case 'DELIVERED': return 'Consegnato';
  //     default: return status;
  //   }
  // }

  // Formatta date in formato dd/MM/yyyy HH:mm
  private formatDate(date: any): string {
    if (!date) return '';
    const d = new Date(date);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }


  resetFilters(): void {
    this.searchDealerCode = '';
    this.selectedDealerCode = '';
    this.palletStatus = '';
    this.searchPalletCode = '';
    this.searchWaybillCode = '';
    this.selectedCarrierCode = '';
    this.warehouseCode = '';
    this.selectedRegionCode = '';
    this.dateDeliveryStart = '';
    this.dateDeliveryEnd = '';
    this.dateStart = '';
    this.dateEnd = '';
    this.onTimeStatus = '';
    this.filterForcedDelivery = false;
    this.palletTypesGroupCode = '';
    this.palletTypesCode = '';
    this.selectedCurrentStatus = [];
    this.selectedBrands = [];
    this.selectedClients = [];

    // Svuota anche i calendari già istanziati, altrimenti gli input
    // mostrerebbero ancora la vecchia data
    this.datePickers.forEach(picker => picker.clear());
    this.clearDateError();
    // La selezione per la chiusura multipla si riferiva ai risultati dei vecchi filtri
    this.clearSelection();

    // Applica di nuovo il filtro (se necessario)
    this.applyFilters();
  }

  setSort(column: string): void {
    if (this.sortState.active === column) {
      // Toggle direction
      this.sortState.direction = this.sortState.direction === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortState.active = column;
      this.sortState.direction = 'asc';
    }
    this.goToFirstPage();
    // L'ordinamento lo applica il backend sull'intero dataset: in locale
    // avrebbe senso solo sulla pagina corrente
    this.loadData();
  }

  onGroupChange() {
    this.palletTypesCode = ''; // reset del secondo filtro
    this.filteredNamesByGroup = this.filteredPalletTypes.filter(
      pt => pt.palletTypesGroupCode === this.palletTypesGroupCode
    );
    this.rebuildSelectOptions();
  }

  getFirstWarehouseName(p: ViewOprPallets): string {
    const found = p.comPallets?.find(c => c.palletComplementaryWarehouseCode);
    return found ? found.warehouseCodeName : '';
  }

  getFirstCarrierName(p: ViewOprPallets): string {
    const found = p.comPallets?.find(c => c.palletComplementaryCarrierCode);
    return found ? found.carrierName : '';
  }


}

